import React, { useState } from 'react';

interface Tag {
  id: string;
  name: string;
  color?: string;
}

interface TagsManagerProps {
  tags: Tag[];
  availableTags: Tag[];
  onAddTag: (tagId: string) => Promise<void>;
  onRemoveTag: (tagId: string) => Promise<void>;
  onCreateTag?: (name: string, color: string) => Promise<Tag>;
  loading?: boolean;
}

const TAG_COLORS = [
  { name: 'Gray', value: 'gray', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
  { name: 'Red', value: 'red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  { name: 'Orange', value: 'orange', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  { name: 'Amber', value: 'amber', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  { name: 'Yellow', value: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
  { name: 'Lime', value: 'lime', bg: 'bg-lime-100', text: 'text-lime-700', border: 'border-lime-200' },
  { name: 'Green', value: 'green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  { name: 'Emerald', value: 'emerald', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  { name: 'Teal', value: 'teal', bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200' },
  { name: 'Cyan', value: 'cyan', bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' },
  { name: 'Sky', value: 'sky', bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-200' },
  { name: 'Blue', value: 'blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  { name: 'Indigo', value: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
  { name: 'Violet', value: 'violet', bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200' },
  { name: 'Purple', value: 'purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  { name: 'Fuchsia', value: 'fuchsia', bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
  { name: 'Pink', value: 'pink', bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200' },
  { name: 'Rose', value: 'rose', bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' }
];

const TagsManager: React.FC<TagsManagerProps> = ({
  tags,
  availableTags,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  loading
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('blue');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getTagClasses = (color?: string) => {
    const colorConfig = TAG_COLORS.find(c => c.value === color) || TAG_COLORS[0];
    return `${colorConfig.bg} ${colorConfig.text} border ${colorConfig.border}`;
  };

  const filteredAvailableTags = availableTags
    .filter(tag => !tags.find(t => t.id === tag.id))
    .filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleAddTag = async (tagId: string) => {
    setSubmitting(true);
    try {
      await onAddTag(tagId);
      setShowDropdown(false);
      setSearchQuery('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    setSubmitting(true);
    try {
      await onRemoveTag(tagId);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTag = async () => {
    if (!onCreateTag || !newTagName.trim()) return;
    
    setSubmitting(true);
    try {
      const newTag = await onCreateTag(newTagName.trim(), newTagColor);
      await onAddTag(newTag.id);
      setNewTagName('');
      setNewTagColor('blue');
      setShowCreateModal(false);
      setShowDropdown(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Current Tags */}
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag.id}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${getTagClasses(tag.color)}`}
          >
            <span>{tag.name}</span>
            <button
              onClick={() => handleRemoveTag(tag.id)}
              disabled={loading || submitting}
              className="hover:bg-black/10 rounded-full p-0.5 transition-colors disabled:opacity-50"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}

        {tags.length === 0 && (
          <span className="text-sm text-gray-500">No tags assigned</span>
        )}
      </div>

      {/* Add Tag Button */}
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add tag</span>
        </button>

        {/* Dropdown */}
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />

            {/* Dropdown Menu */}
            <div className="absolute left-0 mt-2 w-64 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
              {/* Search */}
              <div className="p-3 border-b border-gray-100">
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              {/* Tag List */}
              <div className="max-h-60 overflow-y-auto">
                {filteredAvailableTags.length > 0 ? (
                  <div className="py-1">
                    {filteredAvailableTags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => handleAddTag(tag.id)}
                        disabled={submitting}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagClasses(tag.color)}`}>
                          {tag.name}
                        </span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-8 text-center text-sm text-gray-500">
                    No tags found
                  </div>
                )}
              </div>

              {/* Create New Tag */}
              {onCreateTag && (
                <div className="p-3 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setShowCreateModal(true);
                      setShowDropdown(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="font-medium">Create new tag</span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Tag Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowCreateModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Create New Tag</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tag Name
                </label>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="e.g., High Priority"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewTagColor(color.value)}
                      className={`w-8 h-8 rounded-full ${color.bg} border-2 ${
                        newTagColor === color.value ? 'border-gray-900' : 'border-transparent'
                      } transition-all hover:scale-110`}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview
                </label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getTagClasses(newTagColor)}`}>
                  {newTagName || 'Tag name'}
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={submitting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTag}
                disabled={submitting || !newTagName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsManager;
